import { KeyboardArrowDown } from "@mui/icons-material";
import { Box } from "@mui/material";
import { grey } from "@mui/material/colors";
import { isNil } from "lodash";
import { useSelector } from "react-redux";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { documentStatusLabel } from "../../../constants/selectOptions/selectOptionsDocumentStatus";
import { getUserFullNameFromEmail } from "../../../utils/functions/getUserFullNameFromEmail";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

// Pill colors per documentStatus value. Exported so consumers (eg. inspector
// pane status banner, status edit popover) can render the same pill outside
// of the AG Grid cell.
export const STATUS_STYLES = {
  // Grey, not blue: "New Upload" is the ABSENCE of review, so it shouldn't
  // carry a color of its own competing with the statuses that mean something
  // happened. Same grey the Documents header bar paints its New Upload
  // segment with, so the pill and the chart agree.
  "New Upload": {
    dot: "#CBD5E1",
    bg: "#F8FAFC",
    fg: "#475569",
    border: "#E2E8F0",
  },
  "Pending Review": {
    dot: "#EF9F27",
    bg: "#FAEEDA",
    fg: "#633806",
    border: "#FAC775",
  },
  "Initial Reviewed": {
    dot: "#EF9F27",
    bg: "#FAEEDA",
    fg: "#633806",
    border: "#FAC775",
  },
  "Legal Reviewed": {
    dot: "#EF9F27",
    bg: "#FAEEDA",
    fg: "#633806",
    border: "#FAC775",
  },
  // Processor-reviewed — a distinct intermediate review stage (indigo), sitting
  // between "Pending Review" (amber) and "Underwriter Accepted" (green).
  "Processor Reviewed": {
    dot: "#7C6BD6",
    bg: "#EEEBFA",
    fg: "#372A78",
    border: "#CBC1EF",
  },
  // Incomplete rides the same red tone as Rejected / Expired — it IS a
  // problem status — but shifted a few degrees toward orange so the two
  // aren't indistinguishable pink pills sitting next to each other in a list.
  Incomplete: {
    dot: "#E2603E",
    bg: "#FCEFE9",
    fg: "#7A3416",
    border: "#F7CDB6",
  },
  Rejected: { dot: "#E24B4A", bg: "#FCEBEB", fg: "#791F1F", border: "#F7C1C1" },
  Expired: { dot: "#E24B4A", bg: "#FCEBEB", fg: "#791F1F", border: "#F7C1C1" },
  Accepted: { dot: "#639922", bg: "#EAF3DE", fg: "#27500A", border: "#C0DD97" },
  Uncategorized: {
    dot: "#B4B2A9",
    bg: "#F1EFE8",
    fg: "#5F5E5A",
    border: "#D3D1C7",
  },
};

export const getStatusStyle = (s) =>
  STATUS_STYLES[s] || STATUS_STYLES["Uncategorized"];

const STATUS_SORT_ORDER = [
  "New Upload",
  "Pending Review",
  "Processor Reviewed",
  "Incomplete",
  "Rejected",
  "Accepted",
];

export const statusComparator = (a, b) => {
  const ia = STATUS_SORT_ORDER.indexOf(a);
  const ib = STATUS_SORT_ORDER.indexOf(b);
  const fa = ia === -1 ? STATUS_SORT_ORDER.length : ia;
  const fb = ib === -1 ? STATUS_SORT_ORDER.length : ib;
  return fa - fb;
};

/**
 * Pill-style status cell with optional "By <user> on <date>" subline.
 * AG Grid handles hover via the column-def `tooltipValueGetter` /
 * `tooltipComponent` (set up in `loanFileStatusColumnDef`), not via a
 * MUI Tooltip wrap (which doesn't work reliably inside virtualized cells).
 */
const LoanFileStatusCell = ({ doc, editable = false, compact = false }) => {
  const users = useSelector((state) => state.users);
  if (!doc) return null;
  const status = doc.documentStatus || "Uncategorized";
  const style = getStatusStyle(status);
  const updatedByEmail = doc.documentStatusUpdateBy;
  const updatedAt = doc.documentStatusUpdateDate || doc.modifiedDate;
  const formattedUserName = getUserFullNameFromEmail(users, updatedByEmail);
  // `compact` skips the "By {user}" byline so the cell collapses to a
  // single pill — used by tables that want a tighter row.
  const showByline =
    !compact &&
    status !== "New Upload" &&
    !isNil(formattedUserName) &&
    !isNil(updatedByEmail);

  return (
    <Flx column g={0.25} sx={{ minWidth: 0 }}>
      <Flx
        ac
        g={0.5}
        sx={{
          display: "inline-flex",
          alignSelf: "flex-start",
          px: 0.75,
          py: 0.25,
          borderRadius: "6px",
          bgcolor: style.bg,
          color: style.fg,
          border: `1px solid ${style.border}`,
        }}
      >
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            bgcolor: style.dot,
            flexShrink: 0,
          }}
        />
        <Txt sx={{ fontSize: "11px", color: style.fg, fontWeight: 500 }}>
          {documentStatusLabel(status)}
        </Txt>
        {editable && (
          <KeyboardArrowDown
            sx={{
              fontSize: 14,
              color: style.fg,
              opacity: 0.7,
              ml: -0.25,
            }}
          />
        )}
      </Flx>
      {showByline && (
        <Txt
          sx={{
            fontSize: "9.5px",
            color: grey[600],
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          By {formattedUserName}
          {updatedAt ? ` on ${formatDateTime(updatedAt)}` : ""}
        </Txt>
      )}
    </Flx>
  );
};

/**
 * AG Grid tooltip component for the status column. Renders the resolved
 * "{status} by {full name} on {date}" line. Uses the redux users slice to
 * resolve the email → full name (works because tooltipComponents render
 * inside the React tree).
 */
const LoanFileStatusTooltip = (params) => {
  const users = useSelector((state) => state.users);
  const doc = params?.data;
  if (!doc) return null;
  const status = doc.documentStatus || "Uncategorized";
  const style = getStatusStyle(status);
  const updatedByEmail = doc.documentStatusUpdateBy;
  const updatedAt = doc.documentStatusUpdateDate || doc.modifiedDate;
  const formattedUserName = getUserFullNameFromEmail(users, updatedByEmail);
  const hasUpdater = !isNil(formattedUserName) && !isNil(updatedByEmail);

  return (
    <Box
      sx={{
        background: "#0f172a",
        color: "#f8fafc",
        border: `1px solid ${grey[800]}`,
        borderRadius: 1,
        px: 1,
        py: 0.75,
        fontSize: "11.5px",
        lineHeight: 1.4,
        maxWidth: 320,
      }}
    >
      <Flx ac g={0.5} sx={{ mb: hasUpdater ? 0.25 : 0 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: style.dot,
            flexShrink: 0,
          }}
        />
        <Box component="span" sx={{ fontWeight: 600 }}>
          {documentStatusLabel(status)}
        </Box>
      </Flx>
      {hasUpdater && (
        <Box sx={{ color: grey[300] }}>
          By {formattedUserName}
          {updatedAt ? ` on ${formatDateTime(updatedAt)}` : ""}
        </Box>
      )}
    </Box>
  );
};

const buildStatusTooltipString = (params) => {
  const doc = params?.data;
  if (!doc) return "";
  const status = doc.documentStatus || "Uncategorized";
  const updatedByEmail = doc.documentStatusUpdateBy;
  const updatedAt = doc.documentStatusUpdateDate || doc.modifiedDate;
  const label = documentStatusLabel(status);
  if (!updatedByEmail) return label;
  return `${label} by ${updatedByEmail}${
    updatedAt ? ` on ${formatDateTime(updatedAt)}` : ""
  }`;
};

/**
 * AG Grid column-def factory for the document status column. Returns a base
 * def with valueGetter/comparator/renderer wired up — spread it and override
 * any field (eg. `rowGroup`, `hide`, `editable`, `cellEditor`) for the
 * specific table:
 *
 *   const def = loanFileStatusColumnDef({ editable: editing });
 */
export const loanFileStatusColumnDef = (overrides = {}) => ({
  colId: "documentStatus",
  headerName: "Status",
  field: "documentStatus",
  minWidth: 180,
  width: 180,
  flex: 0,
  autoHeight: true,
  cellClass: "ag-cell-vertical-center",
  cellStyle: { paddingTop: 8, paddingBottom: 8 },
  valueGetter: (p) => {
    const v = p.data?.documentStatus;
    return v === undefined || v === null || v === "" ? "Uncategorized" : v;
  },
  comparator: statusComparator,
  cellRenderer: (params) => <LoanFileStatusCell doc={params.data} />,
  tooltipValueGetter: buildStatusTooltipString,
  tooltipComponent: LoanFileStatusTooltip,
  ...overrides,
});

export default LoanFileStatusCell;
